import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Languages, MoreVertical, Check, CheckCheck, Flag, Ban } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { messageAPI, translateAPI, matchAPI, moderationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'sw', name: 'Swahili' },
  { code: 'yo', name: 'Yoruba' },
  { code: 'ha', name: 'Hausa' },
  { code: 'am', name: 'Amharic' },
  { code: 'zu', name: 'Zulu' },
  { code: 'ig', name: 'Igbo' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pt', name: 'Portuguese' },
];

export default function ChatPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, sendWsMessage } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [matchInfo, setMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(null);
  const [showTranslateMenu, setShowTranslateMenu] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    loadChat();
    
    // Listen for WebSocket messages
    const handleWsMessage = (event) => {
      const data = event.detail;
      if (data.type === 'new_message' && data.message.match_id === matchId) {
        setMessages(prev => [...prev, data.message]);
        // Mark as read immediately
        messageAPI.markRead(matchId).catch(console.error);
      } else if (data.type === 'typing' && data.match_id === matchId) {
        setIsTyping(true);
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
      } else if (data.type === 'messages_read' && data.match_id === matchId) {
        // Update all messages to show read status
        setMessages(prev => prev.map(msg => 
          msg.sender_id === user?.user_id ? { ...msg, is_read: true, read_at: data.read_at } : msg
        ));
      }
    };

    window.addEventListener('ws-message', handleWsMessage);
    return () => window.removeEventListener('ws-message', handleWsMessage);
  }, [matchId, user?.user_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChat = async () => {
    try {
      const [messagesRes, matchesRes] = await Promise.all([
        messageAPI.get(matchId),
        matchAPI.getAll()
      ]);
      
      setMessages(messagesRes.data.messages || []);
      
      const match = matchesRes.data.matches.find(m => m.match_id === matchId);
      setMatchInfo(match);
      
      // Mark messages as read when loading chat
      messageAPI.markRead(matchId).catch(console.error);
    } catch (error) {
      console.error('Failed to load chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    try {
      const response = await messageAPI.send({
        match_id: matchId,
        content: newMessage.trim(),
        message_type: 'text'
      });

      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleTyping = () => {
    const now = Date.now();
    // Only send typing indicator every 2 seconds
    if (now - lastTypingSentRef.current > 2000) {
      lastTypingSentRef.current = now;
      messageAPI.sendTyping(matchId).catch(console.error);
    }
  };

  const translateMessage = async (messageId, content, targetLang) => {
    setTranslating(messageId);
    setShowTranslateMenu(null);
    
    try {
      const response = await translateAPI.translate({
        text: content,
        target_language: targetLang
      });

      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId 
          ? { ...msg, translated: response.data.translated_text, translated_lang: targetLang }
          : msg
      ));
    } catch (error) {
      console.error('Translation failed:', error);
      toast.error('Translation failed');
    } finally {
      setTranslating(null);
    }
  };

  const handleReport = async () => {
    if (!matchInfo?.other_user?.user_id) return;
    
    try {
      await moderationAPI.reportUser({
        reported_user_id: matchInfo.other_user.user_id,
        reason: 'harassment',
        description: 'Reported from chat'
      });
      toast.success('User reported. Our team will review this.');
      setShowMenu(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report user');
    }
  };

  const handleBlock = async () => {
    if (!matchInfo?.other_user?.user_id) return;
    
    try {
      await moderationAPI.blockUser({
        blocked_user_id: matchInfo.other_user.user_id
      });
      toast.success('User blocked');
      navigate('/matches');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/matches')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          {matchInfo && (
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gold/30">
                <img
                  src={matchInfo.other_user.photo || 'https://via.placeholder.com/40'}
                  alt={matchInfo.other_user.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h1 className="font-body font-semibold text-white">
                  {matchInfo.other_user.name}
                </h1>
                {isTyping && (
                  <p className="text-xs text-gold animate-pulse">typing...</p>
                )}
              </div>
              
              {/* Menu button */}
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-dark-elevated border border-white/10 rounded-lg shadow-xl py-1 min-w-[160px] z-50">
                    <button
                      onClick={handleReport}
                      className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/5 flex items-center gap-2"
                    >
                      <Flag className="w-4 h-4 text-yellow-500" />
                      Report User
                    </button>
                    <button
                      onClick={handleBlock}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-white/5 flex items-center gap-2"
                    >
                      <Ban className="w-4 h-4" />
                      Block User
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        <AnimatePresence>
          {messages.map((message, index) => {
            const isMine = message.sender_id === user?.user_id;
            return (
              <motion.div
                key={message.message_id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`flex mb-4 ${isMine ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`relative max-w-[80%] group ${isMine ? 'order-1' : ''}`}>
                  <div
                    className={`px-4 py-3 rounded-2xl ${
                      isMine
                        ? 'bg-gold text-black rounded-br-none'
                        : 'bg-dark-elevated text-white rounded-bl-none border border-white/10'
                    }`}
                  >
                    <p className="font-body text-sm">{message.content}</p>
                    
                    {message.translated && (
                      <div className="mt-2 pt-2 border-t border-black/20">
                        <p className="text-xs opacity-70 mb-1">
                          Translated to {message.translated_lang}:
                        </p>
                        <p className="font-body text-sm italic">{message.translated}</p>
                      </div>
                    )}
                  </div>

                  {/* Translate button */}
                  {!isMine && (
                    <div className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setShowTranslateMenu(showTranslateMenu === message.message_id ? null : message.message_id)}
                        className="p-1 text-gray-400 hover:text-gold transition-colors"
                        disabled={translating === message.message_id}
                      >
                        {translating === message.message_id ? (
                          <div className="w-4 h-4 border border-gold border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Languages className="w-4 h-4" />
                        )}
                      </button>

                      {/* Language menu */}
                      {showTranslateMenu === message.message_id && (
                        <div className="absolute right-0 top-full mt-1 bg-dark-elevated border border-white/10 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
                          {LANGUAGES.map(lang => (
                            <button
                              key={lang.code}
                              onClick={() => translateMessage(message.message_id, message.content, lang.name)}
                              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/5 transition-colors"
                            >
                              {lang.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Timestamp and read receipt */}
                  <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                    <p className="text-xs text-gray-500">
                      {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {isMine && (
                      <span className="text-gray-400">
                        {message.is_read ? (
                          <CheckCheck className="w-4 h-4 text-gold" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        
        {/* Typing indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start mb-4"
          >
            <div className="bg-dark-elevated rounded-2xl rounded-bl-none border border-white/10 px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 bg-dark/95 backdrop-blur-xl border-t border-white/10 p-4 safe-bottom">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            onKeyUp={handleTyping}
            placeholder="Type a message..."
            className="flex-1 bg-dark-elevated border border-white/10 rounded-full px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-gold/50 font-body"
            data-testid="message-input"
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className="w-12 h-12 rounded-full btn-gold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
